import * as R from 'ramda';
import { AppState } from '../redux/appState';
import { ActionType } from '../redux/actionType';
import { ApiLoading } from './apiLoading';
import { ApiError } from './apiError';


/**
 * Makes a new path from 1 or more path segments.
 */
export const pathCombine = (
  ...segments: Array<string | number>
): string => {
  const fullPath = segments.join('/');
  const deduped  = fullPath.split('/').filter(segment => !!segment).join('/');

  const prefixMatch             = deduped.match(/^https?:\/(?!\/)/);
  const withDoubleSlashedPrefix = !!prefixMatch ? deduped.replace(prefixMatch[0], prefixMatch[0] + '/') : deduped;

  const startsWithSlash = fullPath.startsWith('/');
  return startsWithSlash ? `/${withDoubleSlashedPrefix}` : withDoubleSlashedPrefix;
};


/**
 * When we store loading/error information into Redux, we need a key to associate that information with for easy lookup.
 * This function makes that key.
 */
export const keyFor = (
  actionType: ActionType,
  requestId: string | number | ActionType,
): string => `${actionType}-${requestId}`;


/**
 * Adds loading state to AppState, producing a new AppState. This also removes error from app state, as it doesn't make
 * sense to have both loading and error.
 * @param state - The app state to use to make a new App state
 * @param actionType - The Redux action type that triggered this thing
 * @param requestId - the optional unique ID of the request (defaults to ActionType)
 * @param extras - Any extras you want to track with the error
 */
export const addLoading = (
  state: AppState,
  actionType: ActionType,
  requestId: string | number | undefined = undefined,
  extras?: any,
): AppState => {
  const key = keyFor(actionType, requestId ?? actionType);

  return R.dissocPath(
    ['error', key],
    R.assocPath(
      ['loading', key],
      {
        requestId:  requestId,
        actionType: actionType,
        extras:     extras,
      } as ApiLoading, // Cast so the type checker gets angry if we ever change ApiLoading
      state,
    ),
  );
};


/**
 * Produces a new app state without the given loading information inside
 * @param state - The app state to use to make a new App state
 * @param actionType - The Redux action type that triggered this thing
 * @param requestId - the optional unique ID of the request (defaults to ActionType)
 */
export const removeLoading = (
  state: AppState,
  actionType: ActionType,
  requestId: string | number | undefined = undefined,
): AppState => R.dissocPath(['loading', keyFor(actionType, requestId ?? actionType)], state);


/**
 * Retrieves loading information (if it exists) from Redux for the specified request
 * @param state - The app state to use to make a new App state
 * @param actionType - The Redux action type that triggered this thing
 * @param requestId - the optional unique ID of the request (defaults to ActionType)
 */
export const getLoading = (
  state: AppState,
  actionType: ActionType,
  requestId: string | number | undefined = undefined,
): ApiLoading | undefined => state.loading[keyFor(actionType, requestId ?? actionType)];


/**
 * Adds an error to AppState, producing a new AppState. This also removes loading from app state, as it doesn't make
 * sense to have both loading and error.
 * @param state - The app state to use to make a new App state
 * @param actionType - The Redux action type that triggered this thing
 * @param errorMsg - The error message proper
 * @param requestId - the optional unique ID of the request (defaults to ActionType)
 * @param extras - Any extras you want to track with the error
 */
export const addError = (
  state: AppState,
  actionType: ActionType,
  errorMsg: string,
  requestId: string | number | undefined = undefined,
  extras?: any,
): AppState => {
  const key = keyFor(actionType, requestId ?? actionType);

  return R.dissocPath(
    ['loading', key],
    R.assocPath(
      ['error', key],
      {
        requestId:  requestId,
        actionType: actionType,
        errorMsg:   errorMsg,
        extras:     extras,
      } as ApiError, // Cast so the type checker gets angry if we ever change ApiError
      state,
    ),
  );
};


/**
 * Produces a new app state without the given error information inside
 * @param state - The app state to use to make a new App state
 * @param actionType - The Redux action type that triggered this thing
 * @param requestId - the optional unique ID of the request (defaults to ActionType)
 */
export const removeError = (
  state: AppState,
  actionType: ActionType,
  requestId: string | number | undefined = undefined,
): AppState => R.dissocPath(['error', keyFor(actionType, requestId ?? actionType)], state);


/**
 * Retrieves error information (if it exists) from Redux for the specified request
 * @param state - The app state to use to make a new App state
 * @param actionType - The Redux action type that triggered this thing
 * @param requestId - the optional unique ID of the request (defaults to ActionType)
 */
export const getError = (
  state: AppState,
  actionType: ActionType,
  requestId: string | number | undefined = undefined,
): ApiError | undefined => state.error[keyFor(actionType, requestId ?? actionType)];


/**
 * Removes both the loading and error info from state for the specified request, producing a new AppState
 * @param state - The app state to use to make a new App state
 * @param actionType - The Redux action type that triggered this thing
 * @param requestId - the optional unique ID of the request (defaults to ActionType)
 */
export const noLoadingNoError = (
  state: AppState,
  actionType: ActionType,
  requestId: string | number | undefined = undefined,
): AppState => {
  const key = keyFor(actionType, requestId ?? actionType);

  return R.dissocPath(['error', key], R.dissocPath(['loading', key], state));
};
